// @ts-nocheck
import { useChatContext } from 'stream-chat-react';

import { ChatType } from '@common/constants/ChatTypes';

interface Group {
	official_name: string;
}

interface ProgramGroup {
	group_official_name: string;
}

interface Student {
	id: number;
	first_name: string;
	last_name: string;
	groups?: Group[];
	program_groups?: ProgramGroup[];
}

interface Metadata {
	user_types?: string;
	groups?: Group[];
	program_groups?: ProgramGroup[];
	students?: { [label: string]: Student };
}

interface ChannelData {
	type?: string;
	metadata: Metadata;
}

interface Channel {
	data?: ChannelData;
	state: any;
}

type ChatData = {
	students: { label: string; value: string; userLabel?: string; userFullName?: string }[];
};

/**
 * Generates detailed data for a chat channel based on the type of chat and metadata provided.
 * The data is only generated if the channel type is "messaging".
 *
 * @returns {ChatData | undefined} - The structured data for rendering, or undefined
 * if the type is not "messaging".
 */

function useChatDescription(): ChatData | undefined {
	const { channel } = useChatContext() as unknown as {
		channel: Channel;
	};

	if (channel?.data?.type !== 'messaging') return undefined;

	const userType = channel.data.metadata?.user_types;

	switch (userType) {
		case ChatType.MentorStudent: {
			const student = {
				value: Object.values(channel.data.metadata?.groups || [])
					.map((group) => group.official_name)
					.join(', '),
				label: 'Groups',
			};
			return { students: [student] };
		}

		case ChatType.TeacherStudent: {
			const student = {
				value: Object.values(channel.data.metadata?.program_groups || [])
					.map((programGroup) => programGroup.group_official_name)
					.join(', '),
				label: 'Program',
			};
			return { students: [student] };
		}

		case ChatType.MentorParent: {
			const students = Object.values(channel.data.metadata?.students || []);

			const result = {
				userLabel: 'Students',
				userFullName: Object.values(channel.data.metadata?.students || [])
					.map((student) => `${student.first_name} ${student.last_name}`)
					.join(', '),
				value: students
					.flatMap((student) => Object.values(student.groups || []))
					.map((group) => group.official_name)
					.join(', '),
				label: 'Groups',
			};
			return { students: [result] };
		}

		case ChatType.TeacherParent: {
			const students = Object.values(channel.data.metadata?.students || []);

			const result = {
				userLabel: 'Students',
				userFullName: Object.values(channel.data.metadata?.students || [])
					.map((student) => `${student.first_name} ${student.last_name}`)
					.join(', '),
				value: students
					.flatMap((student) =>
						Object.values(student.program_groups || []).map(
							(programGroup) => programGroup.group_official_name,
						),
					)
					.join(', '),
				label: 'Programs',
			};
			return { students: [result] };
		}

		default:
			return undefined;
	}
}

export default useChatDescription;
